import { localeFor } from "../lib/languages";
import { ui, localizedCopies } from "../lib/localization";
import {
  BrainCircuit,
  Check,
  ChevronDown,
  Clock3,
  Code2,
  Coins,
  Eye,
  Search,
  Star,
  Wrench,
  X,
} from "lucide-react";
import { useId, useLayoutEffect, useMemo, useState, type KeyboardEvent } from "react";
import { getCapabilityLabel, getModelDescription, translations, type Language } from "../i18n";
import { filterModelCollection, formatContextWindow, formatPrice, searchModels, sortModels, type ModelCollection, type ModelSortOrder, type ModelUseCase } from "../lib/models";
import type { ModelInfo } from "../types";
import { ContextGlyph } from "./ProductIcons";
import { ProviderLogo } from "./ProviderLogo";
import { isDialogBackdropClick, useModalDialog } from "../lib/use-modal-dialog";
import "./model-picker.css";

type Filter = "all" | "reasoning" | "vision" | "tools" | "paid" | "lora";

const filterItems: Filter[] = ["all", "reasoning", "vision", "tools", "paid", "lora"];

const capabilityIcon = {
  reasoning: BrainCircuit,
  vision: Eye,
  tools: Wrench,
  coding: Code2,
};

const createPickerCopy = (language: Language) => ({
  collections: {
    all: ui(language, "All models"),
    favorites: ui(language, "Favorites"),
    recent: ui(language, "Recent"),
  },
  collectionLabel: ui(language, "Model collection"),
  useCaseLabel: ui(language, "Use case"),
  sortLabel: ui(language, "Sort"),
  useCases: {
    everyday: ui(language, "Everyday chat"),
    coding: ui(language, "Coding"),
    vision: ui(language, "Images"),
  },
  useCaseHints: {
    everyday: ui(language, "All models support text chat. Favorites appear first in the default order."),
    coding: ui(language, "Models explicitly tagged for coding in the catalog."),
    vision: ui(language, "Models that support image input."),
  },
  orders: {
    recommended: ui(language, "Default order"),
    recent: ui(language, "Recently used first"),
    "output-asc": ui(language, "Output price: low to high"),
    "output-desc": ui(language, "Output price: high to low"),
    "input-asc": ui(language, "Input price: low to high"),
    "input-desc": ui(language, "Input price: high to low"),
  },
  expand: ui(language, "Show details"),
  collapse: ui(language, "Hide details"),
  cached: ui(language, "Cached input"),
  priceUnit: ui(language, "Prices per million tokens"),
  recent: ui(language, "Recently used"),
  selected: ui(language, "Selected"),
  emptyFavorites: ui(language, "No favorites yet. Star a model to find it here next time."),
  emptyRecent: ui(language, "No recent models yet. Models appear here after you select and use them."),
  noMatches: ui(language, "No models match these filters. Try clearing them."),
  reset: ui(language, "Clear filters"),
});

const pickerCopy = localizedCopies(createPickerCopy);

interface ModelPickerProps {
  models: ModelInfo[];
  selectedId: string;
  favoriteIds: string[];
  recentModelIds?: string[];
  syncedAt: string;
  language: Language;
  onSelect: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onClose: () => void;
}

export function ModelPicker({
  models,
  selectedId,
  favoriteIds,
  recentModelIds = [],
  syncedAt,
  language,
  onSelect,
  onToggleFavorite,
  onClose,
}: ModelPickerProps) {
  const t = translations[language].picker;
  const copy = pickerCopy[language];
  const detailsPrefix = useId();
  const dialogRef = useModalDialog(true);
  useLayoutEffect(() => {
    dialogRef.current?.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
  }, [dialogRef]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [collection, setCollection] = useState<ModelCollection>("all");
  const [useCase, setUseCase] = useState<ModelUseCase>("everyday");
  const [order, setOrder] = useState<ModelSortOrder>("recommended");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const visibleModels = useMemo(() => {
    const localizedModels = models.map((model) => ({
      ...model,
      description: getModelDescription(model, language),
    }));
    const filtered = filterModelCollection(searchModels(localizedModels, query, filter), collection, useCase, favoriteIds, recentModelIds);
    return sortModels(filtered, { favoriteIds, recentModelIds, order: collection === "recent" && order === "recommended" ? "recent" : order });
  }, [models, query, filter, collection, useCase, order, favoriteIds, recentModelIds, language]);

  const focusModel = (index: number) => {
    const buttons = dialogRef.current?.querySelectorAll<HTMLButtonElement>(".model-select");
    if (!buttons?.length) return;
    const button = buttons[Math.max(0, Math.min(index, buttons.length - 1))];
    button.focus();
    button.scrollIntoView?.({ block: "nearest" });
  };
  const navigateModels = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!(event.target instanceof HTMLButtonElement) || !event.target.classList.contains("model-select")) return;
    const buttons = [...event.currentTarget.querySelectorAll(".model-select")];
    const index = buttons.indexOf(event.target);
    const target = event.key === "ArrowDown" ? index + 1 : event.key === "ArrowUp" ? index - 1 : event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : null;
    if (target != null) { event.preventDefault(); focusModel(target); }
  };
  const resetFilters = () => { setQuery(""); setFilter("all"); setUseCase("everyday"); setCollection("all"); };

  return (
      <dialog
        ref={dialogRef}
        aria-label={t.dialogLabel}
        aria-modal="true"
        className="model-picker model-picker-enhanced"
        onCancel={onClose}
        onClick={(event) => { if (isDialogBackdropClick(event)) onClose(); }}
      >
        <header className="picker-header">
          <div>
            <span className="eyebrow">{t.eyebrow}</span>
            <h2>{t.title}</h2>
          </div>
          <button className="icon-button" onClick={onClose} type="button" aria-label={t.close}>
            <X size={19} />
          </button>
        </header>

        <div className="picker-toolbar">
        <div className="model-search">
          <Search size={18} />
          <input
            type="search"
            name="model-search"
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => { if (event.key === "ArrowDown" && visibleModels.length) { event.preventDefault(); focusModel(0); } }}
            placeholder={t.searchPlaceholder}
            aria-label={t.searchAria}
            aria-autocomplete="none"
            autoComplete="off"
            data-form-type="other"
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore="true"
          />
          <kbd>⌘ K</kbd>
        </div>

        <div className="picker-collections" role="group" aria-label={copy.collectionLabel}>
          {(["all", "favorites", "recent"] as const).map((item) => (
            <button type="button" key={item} aria-pressed={collection === item} onClick={() => setCollection(item)}>
              {item === "favorites" && <Star size={15} />}
              {item === "recent" && <Clock3 size={15} />}
              {copy.collections[item]}
            </button>
          ))}
        </div>
        <div className="picker-controls">
          <label><span>{copy.useCaseLabel}</span><select value={useCase} onChange={(event) => setUseCase(event.target.value as ModelUseCase)}>
            {Object.entries(copy.useCases).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select></label>
          <label><span>{copy.sortLabel}</span><select value={order} onChange={(event) => setOrder(event.target.value as ModelSortOrder)}>
            {Object.entries(copy.orders).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select></label>
        </div>
        <p className="picker-use-case-hint">{copy.useCaseHints[useCase]}</p>

        <div className="filter-row" aria-label={t.filtersAria}>
          {filterItems.map((item) => (
            <button
              className={filter === item ? "filter-pill active" : "filter-pill"}
              key={item}
              onClick={() => setFilter(item)}
              aria-pressed={filter === item}
              type="button"
            >
              {t.filters[item]}
            </button>
          ))}
        </div>
        </div>

        <div className="model-grid" onKeyDown={navigateModels}>
          {visibleModels.map((model) => {
            const expanded = expandedIds.has(model.id);
            const detailsId = `${detailsPrefix}-${encodeURIComponent(model.id)}`;
            return (
            <article
              className={selectedId === model.id ? "model-card selected" : "model-card"}
              key={model.id}
            >
              <button type="button" className="model-select" aria-pressed={selectedId === model.id}
                aria-label={model.name} onClick={() => onSelect(model.id)} />
              <div className="model-card-top">
                <div className="provider-mark" title={model.provider}>
                  <ProviderLogo provider={model.provider} fallbackClassName="provider-fallback-glyph" />
                </div>
                <div className="model-card-title">
                  <h3>{model.name}</h3>
                  <span>{model.provider}{recentModelIds.includes(model.id) ? ` · ${copy.recent}` : ""}</span>
                </div>
                <button
                  type="button"
                  className={favoriteIds.includes(model.id) ? "favorite active" : "favorite"}
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleFavorite(model.id);
                  }}
                  aria-label={favoriteIds.includes(model.id) ? t.removeFavorite : t.addFavorite}
                  aria-pressed={favoriteIds.includes(model.id)}
                >
                  <Star size={16} fill={favoriteIds.includes(model.id) ? "currentColor" : "none"} />
                </button>
              </div>
              {!expanded && <p className="model-short-description">{getModelDescription(model, language)}</p>}
              <div className="capability-row">
                <span className="capability"><ContextGlyph />{formatContextWindow(model.contextWindow, language)}</span>
                {model.capabilities.map((capability) => {
                  const Icon = capabilityIcon[capability];
                  return <span className="capability" key={capability}><Icon size={13} />{getCapabilityLabel(capability, language)}</span>;
                })}
                {model.paid && <span className="capability paid"><Coins size={13} />{translations[language].paid}</span>}
                {model.lora && <span className="capability">LoRA</span>}
              </div>
              <div className="price-row">
                <span>{t.input} <bdi dir="ltr">{formatPrice(model.prices.input)} / M</bdi></span>
                <span>{t.output} <bdi dir="ltr">{formatPrice(model.prices.output)} / M</bdi></span>
                {selectedId === model.id && <Check className="selected-check" size={17} aria-label={copy.selected} />}
              </div>
              <div className="model-details-action">
                <button className="model-details-toggle" type="button" aria-expanded={expanded} aria-controls={detailsId}
                  onClick={() => setExpandedIds((previous) => { const next = new Set(previous); if (next.has(model.id)) next.delete(model.id); else next.add(model.id); return next; })}>
                  {expanded ? copy.collapse : copy.expand}<ChevronDown size={15} />
                </button>
              </div>
              <div className="model-details-content" id={detailsId} hidden={!expanded}>
                <p>{getModelDescription(model, language)}</p>
                <code>{model.id}</code>
                {model.prices.cachedInput != null && <span>{copy.cached} <bdi dir="ltr">{formatPrice(model.prices.cachedInput)} / M</bdi></span>}
                <span>{copy.priceUnit}</span>
              </div>
            </article>
          ); })}
          {visibleModels.length === 0 && (
            <div className="empty-results">
              <Search size={22} />
              <p>{collection === "favorites" && !favoriteIds.some((id) => models.some((model) => model.id === id)) ? copy.emptyFavorites : collection === "recent" && !recentModelIds.some((id) => models.some((model) => model.id === id)) ? copy.emptyRecent : copy.noMatches}</p>
              <button type="button" className="picker-reset" onClick={resetFilters}>{copy.reset}</button>
            </div>
          )}
        </div>

        <footer className="picker-footer">
          <span role="status">{t.modelCount(visibleModels.length, models.length)}</span>
          <span>{t.catalogSynced(new Date(syncedAt).toLocaleDateString(localeFor(language)))}</span>
        </footer>
      </dialog>
  );
}
